import { CodeOutlined, ContentCopyOutlined, ContentPasteOutlined, RouterOutlined } from '@mui/icons-material'
import { Stack } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import { ModbusTestingScriptDialog } from '@/features/controller/configuration/components/dialogs/ModbusTestingScriptDialog'
import { NodeActions, NodeMenuItemType } from '@/features/controller/configuration/components/tree/NodeActions'
import {
  readControllerConfigurationFromClipboard,
  writeControllerConfigurationToClipboard,
} from '@/features/controller/configuration/models/controllerConfigClipboard'
import type {
  ControllerConfigurationModel,
  ControllerConfigurationSourceUpdate,
} from '@/features/controller/configuration/models/controllerConfiguration'
import { generateModbusTestingScript } from '@/features/controller/configuration/utils/modbusTesting'
import type { ControllerDescription } from '@/features/controller/types'

interface ControllerHeaderProps {
  configurationModel: ControllerConfigurationModel
  configurationSourceView: ControllerConfigurationSourceUpdate
  controllerDescription: ControllerDescription
  onConfigurationSourceUpdated: (update: ControllerConfigurationSourceUpdate) => void
}

export function ControllerHeader({
  configurationModel,
  configurationSourceView,
  controllerDescription,
  onConfigurationSourceUpdated,
}: Readonly<ControllerHeaderProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const [modbusTestingScript, setModbusTestingScript] = useState<string | undefined>(undefined)

  return (
    <Stack alignItems="center" direction="row" gap={1}>
      <RouterOutlined />
      {controllerDescription.equipmentNumber}
      <NodeActions
        menuActions={[
          {
            icon: <ContentCopyOutlined />,
            label: t('controllers.configuration.tree.controller_actions.copy_config'),
            perform: async () =>
              await writeControllerConfigurationToClipboard(
                {
                  appJsonText: configurationSourceView.appJsonText,
                  raspiPropertiesText: configurationSourceView.raspiPropertiesText,
                  controllerId: controllerDescription.id,
                  controllerEquipmentNumber: controllerDescription.equipmentNumber,
                },
                pushAlert,
                t,
              ),
            type: NodeMenuItemType.action,
          },
          {
            icon: <ContentPasteOutlined />,
            label: t('controllers.configuration.tree.controller_actions.paste_config'),
            perform: async () => {
              const clipboardData = await readControllerConfigurationFromClipboard(pushAlert, t)
              if (clipboardData) {
                const { appJsonText, raspiPropertiesText, controllerEquipmentNumber } = clipboardData
                onConfigurationSourceUpdated({
                  appJsonText,
                  raspiPropertiesText,
                  comment: `Copied from controller with equipment number ${controllerEquipmentNumber}`,
                })
              }
            },
            type: NodeMenuItemType.action,
          },
          {
            type: NodeMenuItemType.divider,
          },
          {
            icon: <CodeOutlined />,
            label: t('controllers.configuration.tree.controller_actions.generate_modbus_script'),
            perform: async () => {
              setModbusTestingScript(generateModbusTestingScript(controllerDescription, configurationModel))
            },
            type: NodeMenuItemType.action,
          },
        ]}
      />
      {modbusTestingScript && (
        <ModbusTestingScriptDialog
          open={true}
          script={modbusTestingScript}
          onClose={() => setModbusTestingScript(undefined)}
        />
      )}
    </Stack>
  )
}
